import { h } from 'preact';
import qs from 'qs';
import * as BaseComp from 'components/bolt/homeEnt/homeEnt.js';
import { joinClasses, accessNested } from 'utils/utils';
import Close from 'components/bolt/icons/close';
import * as webp from 'utils/bolt/webp';
import formatDateTime from 'utils/formatDateTime';
import CalendarRemindButtons from 'components/bolt/calendarRemind/calendarRemindButtons';
import s from 'components/bolt/homeEnt/homeEnt.sss';

export default class HomeEnt extends BaseComp.default {

	getAllAvailabilities(allRegions, multiRegion, homeEntData = {}, selectedFormat, restrictedAvail) {
		const base = super.getAllAvailabilities(allRegions, multiRegion, homeEntData, selectedFormat, restrictedAvail);
		if (base.includes('comingSoon')) {
			const comingSoonIndex = base.indexOf('comingSoon');
			base.splice(comingSoonIndex, 1);
		}
		return base;
	}

	preselectAvailsFromQuery() {
		if (!window.location.search) {
			return;
		}

		const availMapping = {
			'own-it': 'buy',
			'on-demand': 'stream'
		};
		const queries = qs.parse(window.location.search.replace('?', ''));
		if (queries.availability) {
			const newAvail = availMapping[queries.availability] || queries.availability;
			if (!this.state.availabilitiesToShow?.includes(newAvail)) {
				return;
			}
			const currentAvail = this.state.userSelectedAvailabilities && this.state.userSelectedAvailabilities.join(',');
			if (newAvail !== currentAvail) {
				this.setState({
					userSelectedAvailabilities: [newAvail]
				});
			}
		}
	}

	renderMerchants(data, format, columns, isLoading, currentRegion) {
		if (isLoading) {
			return (
				<div class={s.merchants}>
					{Array(columns === 2 ? 6 : 12).fill('').map((m, i) => <div key={i} class={s.dummyMerchant}></div>)}
				</div>
			);
		}

		const { multiRegion, regions, smallMerchantTiles, activeOverlay } = this.state;
		const isFirstRegion = regions && currentRegion === regions[0];
		const regionCustomisations = accessNested(this.props, 'store.appData.pages.boltHomeEnt.data.customiseRegion', {});
		const regionData = regionCustomisations[currentRegion] || {};
		const fontStyling = accessNested(this.props, 'store.appData.styling.fontVariables', {});
		const logoShade = accessNested(this.props, 'store.appData.styling.logoShade', 'light');

		if (!data || !format || !data[format]) return;

		const { userSelectedAvailabilities } = this.state;
		let merchants = Object.keys(data[format]).filter(m => {
			if (m === 'undefined') return false;

			// don't render the merchant if it has no products to show within the selected availabilities
			let hasProduct = false;

			userSelectedAvailabilities.forEach(a => {
				if (data[format][m][a] && data[format][m][a].length) hasProduct = true;
			});

			if (userSelectedAvailabilities.includes('comingSoon') && data[format][m].comingSoon) {
				// if the comingSoon has expired and there are other avails for the merchant,
				// we don't want to show it under comingSoon
				if (!(data[format][m].comingSoon.expired && (Object.keys(data[format][m]).length > 1))) {
					hasProduct = true;
				}
				// we can still show it under comingSoon if it only has one other property, which is 'order'
				if (data[format][m].comingSoon.expired && (Object.keys(data[format][m]).length === 2) && !!data[format][m].order) {
					hasProduct = true;
				}
			}

			// If merchant has an active 'coming soon', it should not appear in any other availability
			if (data[format][m].comingSoon && !data[format][m].comingSoon.expired && !userSelectedAvailabilities.includes('comingSoon')) {
				hasProduct = false;
			}

			// OVERRIDE FOR THIS APP - let the buy availability show comingSoon too
			if (userSelectedAvailabilities.includes('buy') && data[format][m].comingSoon && !data[format][m].comingSoon.expired) {
				hasProduct = true;
			}

			return hasProduct;
		}).sort((a, b) => {
			if (data[format][a].order && data[format][b].order) {
				return (data[format][a].order - data[format][b].order);
			}
			if (data[format][a].order) {
				return -1;
			}
			if (data[format][b].order) {
				return 1;
			}
			return a.localeCompare(b);
		});

		if (!merchants.length) return;

		const dangling = merchants.length % columns;
		const numDummiesNeeded = !dangling ? 0 : columns - dangling;
		const dummyArray = new Array(numDummiesNeeded).fill('dummyElement');

		if (dummyArray.length) {
			merchants = merchants.concat(dummyArray);
		}

		return (
			<div class={joinClasses(s.merchants, !isFirstRegion && s.extraRegion, smallMerchantTiles && s.smallMerchantTiles)}>
				{multiRegion && (
					<div class={s.regionHeader}>
						<span class={joinClasses(s.regionTitle, fontStyling.homeEntRegionHeader)}>{regionData.title}</span>
						<div class={s.titleLine} />
					</div>
				)}
				{merchants.map((m, i) => {
					if (m === 'dummyElement') return <div key={i} class={s.dummyMerchant} />;

					const logo = this.getLogoSrc(m, data[format][m].logo, currentRegion);
					const fallbackFormat = this.getFallbackFormat();

					const singleLinkOutData = this.getSingleLinkForMerchant(data[format][m], userSelectedAvailabilities);
					const isLoneExpiredComingSoon = data[format][m].comingSoon && data[format][m].comingSoon.expired && (Object.keys(data[format][m]).length === 1 || Object.keys(data[format][m]).length === 2 && !!data[format][m].order);

					let textOverlay = data[format][m].textOverlay;
					if (textOverlay && typeof textOverlay !== 'string') {
						textOverlay = textOverlay.join('');
					}

					return (
						<div
							class={joinClasses(s.merchant, isLoneExpiredComingSoon && s.unclickable)}
							key={`${m}-${currentRegion}`}
							onClick={e => this.openMerchant(data[format][m], logo, singleLinkOutData, regionData, m, currentRegion, i, e)}
							ref={e => this[`$merchantTile${i}`] = e}
						>
							{textOverlay && (
								<div class={joinClasses(s.overlayWrapper, (activeOverlay === i) && s.active, s[logoShade])}>
									<button class={s.overlayButton} onClick={e => this.setActiveOverlay(e, i)}>
										{activeOverlay === i ? <Close /> : <Info />}
									</button>
									<div class={joinClasses(s.overlayContainer, 'smallB')}>
										{textOverlay}
									</div>
								</div>
							)}
							<webp.Picture
								alt={this.getLogoAltText(m, data[format][m])}
								src={logo}
								fallbackFormat={fallbackFormat}
							/>
						</div>
					);
				})}
			</div>
		);
	}

	renderMerchantSection() {
		const { selectedMerchant, selectedMerchantData, selectedLogo, merchantScreenOpen, userSelectedAvailabilities, selectedRegionData } = this.state;
		const availabilities = Object.keys(selectedMerchantData)
			.filter(a => a !== 'textOverlay' && userSelectedAvailabilities.includes(a) && Object.keys(selectedMerchantData[a]).length)
			.sort((a,b) => this.availabilityOrder.indexOf(b) - this.availabilityOrder.indexOf(a));

		let hasComingSoon = availabilities && availabilities.includes('comingSoon') && !selectedMerchantData.comingSoon.expired;
		// OVERRIDE FOR THIS APP - comingSoon might show up in the 'buy' avail, it needs to show cal remind
		// if it is not expired
		if (availabilities && availabilities.includes('buy') && selectedMerchantData.comingSoon && !selectedMerchantData.comingSoon.expired) {
			hasComingSoon = true;
		}
		const copy = accessNested(this.props, 'store.appData.copy.baseCopy', {});
		const logoShade = accessNested(this.props, 'store.appData.styling.logoShade', 'light');
		const merchantDetailsOverrides = accessNested(this.props, 'store.appData.pages.boltHomeEnt.data.overrideMerchantDetails', {});
		const fallbackFormat = this.getFallbackFormat("tt");
		let merchantName, comingSoonData, calRemindData, remindMeDate, formattedDate, availOnDateCopy, calRemindTitle, calRemindBody, chooseCalCopy, siteUrl;

		if (hasComingSoon) {
			comingSoonData = selectedMerchantData.comingSoon || {};
			merchantName = comingSoonData.merchantName ||
											merchantDetailsOverrides[selectedMerchant]?.name ||
											selectedMerchantData.merchantName || '';
			calRemindData = accessNested(this.props, 'store.appData.pages.boltHomeEnt.data.calRemind', {});

			const remindMeDateWithOffset = new Date(comingSoonData.date);
			remindMeDate = new Date( remindMeDateWithOffset.getTime() - remindMeDateWithOffset.getTimezoneOffset() * -60000 );
			formattedDate = formatDateTime(remindMeDate, 'MMM dd', copy.$DATETIME_LOCALE);
			calRemindTitle = accessNested(comingSoonData, 'calendarCustomisation.calRemindTitle') || selectedRegionData.calRemindTitle || copy[calRemindData.title] || calRemindData.title || '';
			siteUrl = accessNested(this.props, 'store.appData.meta.url', '');
			calRemindBody = (accessNested(comingSoonData, 'calendarCustomisation.calRemindBody') || selectedRegionData.calRemindBody || copy[calRemindData.body] || calRemindData.body || '').replace('<merchant>', merchantName).replace('%VANITY_DOMAIN%', siteUrl);
			chooseCalCopy = selectedRegionData.calRemindCta || copy[calRemindData.cta] || calRemindData.cta || '';
			availOnDateCopy = (selectedRegionData.calRemindAvailOnDate || copy[calRemindData.available] || copy.$AVAILABLE_ON_DATE_COPY || '').replace('<date>', formattedDate);
		}

		return (
			<div
				class={joinClasses({
					s,
					container: true,
					hidden: !availabilities.length || !merchantScreenOpen,
					merchantSectionContainer: true
				})}
				ref={e => this.$merchantSectionContainer = e}
				onClick={this.maybeCloseMerchantSection}
			>
				<div class={s.scroller} ref={e => this.$merchantSectionScroller = e} onClick={this.maybeCloseMerchantSection}>
					<div class={joinClasses(s.content, hasComingSoon && s.comingSoonContent)}>
						<div class={joinClasses(s.sticky, hasComingSoon && s.stickyComingSoon)}>
							<webp.Picture class={s.logo} src={selectedLogo} alt={this.getLogoAltText(selectedMerchant, selectedMerchantData)} fallbackFormat={fallbackFormat} />
							<Close class={s.close} onClick={this.closeMerchantSection} />
							{hasComingSoon && <span class={s.availOnDateCopy}>{availOnDateCopy}</span>}
						</div>
						{!hasComingSoon && availabilities.map(availability => this.renderProducts(availability, selectedMerchantData[availability], selectedRegionData))}
						{hasComingSoon &&
							<div class={s.calRemindSection}>
								<CalendarRemindButtons
									remindMeDate={remindMeDate}
									summary={calRemindTitle}
									description={calRemindBody}
									calObj={calRemindData}
									isHomeEnt
									chooseCalCopy={chooseCalCopy}
									iconShade={logoShade}
								/>
							</div>
						}
					</div>
				</div>
			</div>
		);
	}

}
